import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Filter, Download, FileDown, X, Layers, ChevronDown, ChevronRight, Landmark, Store, Package, TrendingUp, DollarSign, FileText, Calendar } from 'lucide-react';
import StatusBadge from '@/components/StatusBadge';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'aprobada', label: 'Aprobada' },
  { value: 'pagada', label: 'Pagada' },
  { value: 'rechazada', label: 'Rechazada' },
];

const GROUP_OPTIONS = [
  { value: 'none', label: 'Sin agrupar', icon: FileText },
  { value: 'banco', label: 'Por banco', icon: Landmark },
  { value: 'tienda', label: 'Por tienda', icon: Store },
  { value: 'proveedor', label: 'Por proveedor', icon: Package },
];

export default function DetailedReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [stores, setStores] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [totals, setTotals] = useState({ count: 0, monto_total: 0, monto_pagado: 0, por_estado: {} });
  const [grupos, setGrupos] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Filtros
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [banco, setBanco] = useState('');
  const [tiendaId, setTiendaId] = useState('');
  const [proveedorSearch, setProveedorSearch] = useState('');
  const [usuarioId, setUsuarioId] = useState('');
  const [estadosSel, setEstadosSel] = useState(new Set(['pendiente', 'aprobada', 'pagada', 'rechazada']));
  const [tipoPago, setTipoPago] = useState('');
  const [groupBy, setGroupBy] = useState('none');

  // Cargar catálogos
  useEffect(() => {
    axios.get(`${API}/stores`).then(r => setStores(r.data || [])).catch(() => {});
    axios.get(`${API}/bancos`).then(r => setBancos(r.data || [])).catch(() => {});
    axios.get(`${API}/reports/detailed/users`).then(r => setUsers(r.data || [])).catch(() => {});
  }, []);

  const buildParams = useCallback((extra = {}) => {
    const params = new URLSearchParams();
    if (fechaInicio) params.set('fecha_inicio', fechaInicio);
    if (fechaFin) params.set('fecha_fin', fechaFin);
    if (banco) params.set('banco', banco);
    if (tiendaId) params.set('tienda_id', tiendaId);
    if (proveedorSearch.trim()) params.set('proveedor_search', proveedorSearch.trim());
    if (usuarioId) params.set('usuario_id', usuarioId);
    if (estadosSel.size > 0 && estadosSel.size < STATUS_OPTIONS.length) {
      params.set('estados', Array.from(estadosSel).join(','));
    }
    if (tipoPago) params.set('tipo_pago', tipoPago);
    if (groupBy && groupBy !== 'none') params.set('group_by', groupBy);
    Object.entries(extra).forEach(([k, v]) => params.set(k, v));
    return params;
  }, [fechaInicio, fechaFin, banco, tiendaId, proveedorSearch, usuarioId, estadosSel, tipoPago, groupBy]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/detailed?${buildParams().toString()}`);
      setPayments(res.data.payments || []);
      setTotals(res.data.totals || { count: 0, monto_total: 0, monto_pagado: 0, por_estado: {} });
      setGrupos(res.data.grupos || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // Auto-fetch al montar y cuando cambien filtros (con debounce ligero)
  useEffect(() => {
    const t = setTimeout(fetchReport, 300);
    return () => clearTimeout(t);
  }, [fetchReport]);

  const toggleEstado = (val) => {
    const s = new Set(estadosSel);
    if (s.has(val)) s.delete(val); else s.add(val);
    setEstadosSel(s);
  };

  const clearFilters = () => {
    setFechaInicio(''); setFechaFin(''); setBanco(''); setTiendaId('');
    setProveedorSearch(''); setUsuarioId(''); setTipoPago(''); setGroupBy('none');
    setEstadosSel(new Set(['pendiente', 'aprobada', 'pagada', 'rechazada']));
  };

  const activeFiltersCount = useMemo(() => {
    let n = 0;
    if (fechaInicio) n++;
    if (fechaFin) n++;
    if (banco) n++;
    if (tiendaId) n++;
    if (proveedorSearch.trim()) n++;
    if (usuarioId) n++;
    if (tipoPago) n++;
    if (estadosSel.size < STATUS_OPTIONS.length) n++;
    return n;
  }, [fechaInicio, fechaFin, banco, tiendaId, proveedorSearch, usuarioId, tipoPago, estadosSel]);

  const handleExport = async () => {
    setDownloading(true);
    try {
      const params = buildParams();
      params.delete('group_by');
      const res = await axios.get(`${API}/reports/detailed/export?${params.toString()}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_detallado_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Reporte exportado');
    } catch (err) {
      toast.error('No se pudo exportar el reporte');
    } finally {
      setDownloading(false);
    }
  };

  const handleExportPdf = async () => {
    setDownloadingPdf(true);
    try {
      const params = buildParams();
      params.delete('group_by');
      const res = await axios.get(`${API}/reports/detailed/export-pdf?${params.toString()}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      // Abrir en nueva pestaña para visualización directa
      window.open(url, '_blank');
      // También ofrecer descarga automática
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_detallado_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('PDF generado');
    } catch (err) {
      toast.error('No se pudo generar el PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="detailed-report-page">
      <div className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-2xl font-display font-bold text-slate-900 dark:text-slate-100">Reporte Detallado</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Filtra por banco, tienda, proveedor y fechas. Agrupa para ver subtotales.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Filtros */}
        <Card className="p-5 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="filters-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <Filter className="h-4 w-4 text-[#2c57c5]" /> Filtros
              {activeFiltersCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-blue-100 dark:bg-blue-950/60 text-[10px] font-semibold text-[#2c57c5] dark:text-blue-300">
                  {activeFiltersCount}
                </span>
              )}
            </h2>
            {activeFiltersCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-slate-500 dark:text-slate-400" data-testid="clear-filters">
                <X className="h-3.5 w-3.5 mr-1" /> Limpiar
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Desde</Label>
              <Input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} data-testid="filter-date-start" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Hasta</Label>
              <Input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} data-testid="filter-date-end" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Banco</Label>
              <Select value={banco || 'all'} onValueChange={(v) => setBanco(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-banco"><SelectValue placeholder="Todos los bancos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los bancos</SelectItem>
                  {bancos.map(b => (
                    <SelectItem key={b.id || b.nombre} value={b.nombre}>{b.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Tienda</Label>
              <Select value={tiendaId || 'all'} onValueChange={(v) => setTiendaId(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-tienda"><SelectValue placeholder="Todas las tiendas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las tiendas</SelectItem>
                  {stores.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Proveedor / NIT</Label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  className="pl-9"
                  placeholder="Buscar por nombre o NIT..."
                  value={proveedorSearch}
                  onChange={(e) => setProveedorSearch(e.target.value)}
                  data-testid="filter-proveedor"
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Usuario que reportó</Label>
              <Select value={usuarioId || 'all'} onValueChange={(v) => setUsuarioId(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-usuario"><SelectValue placeholder="Todos los usuarios" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los usuarios</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name}{u.role ? ` · ${u.role}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Tipo de pago</Label>
              <Select value={tipoPago || 'all'} onValueChange={(v) => setTipoPago(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-tipo-pago"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="contado">Contado</SelectItem>
                  <SelectItem value="credito">Crédito</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Estado toggles */}
          <div className="mt-4">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300 block mb-2">Estados</Label>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map(s => {
                const active = estadosSel.has(s.value);
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => toggleEstado(s.value)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      active
                        ? 'bg-blue-50 dark:bg-blue-950/40 border-blue-300 dark:border-blue-900/60 text-[#2c57c5] dark:text-blue-300'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-700'
                    }`}
                    data-testid={`filter-estado-${s.value}`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Agrupar por + exportar */}
          <div className="mt-4 flex flex-wrap gap-3 items-center justify-between border-t border-slate-100 dark:border-slate-800 pt-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-1">
                <Layers className="h-3.5 w-3.5" /> Agrupar:
              </span>
              {GROUP_OPTIONS.map(g => {
                const Icon = g.icon;
                const active = groupBy === g.value;
                return (
                  <button
                    key={g.value}
                    type="button"
                    onClick={() => setGroupBy(g.value)}
                    className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                      active
                        ? 'bg-[#2c57c5] border-[#2c57c5] text-white'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:border-slate-300'
                    }`}
                    data-testid={`group-${g.value}`}
                  >
                    <Icon className="h-3 w-3" /> {g.label}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2">
              <Button onClick={handleExportPdf} disabled={downloadingPdf || totals.count === 0} variant="outline" className="border-red-500 text-red-600 hover:bg-red-50 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/40" data-testid="export-pdf">
                <FileDown className="h-4 w-4 mr-2" />
                {downloadingPdf ? 'Generando...' : 'PDF'}
              </Button>
              <Button onClick={handleExport} disabled={downloading || totals.count === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="export-excel">
                <Download className="h-4 w-4 mr-2" />
                {downloading ? 'Exportando...' : 'Excel'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Resumen */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="summary-cards">
          <SummaryCard label="Total de pagos" value={totals.count} icon={FileText} tone="blue" />
          <SummaryCard label="Monto total" value={`Q${totals.monto_total.toLocaleString('es-GT', { minimumFractionDigits: 2 })}`} icon={DollarSign} tone="blue" />
          <SummaryCard label="Efectivamente pagado" value={`Q${totals.monto_pagado.toLocaleString('es-GT', { minimumFractionDigits: 2 })}`} icon={TrendingUp} tone="emerald" />
          <SummaryCard label="Pendiente" value={`Q${(totals.monto_total - totals.monto_pagado).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`} icon={Calendar} tone="amber" />
        </div>

        {/* Resultados */}
        {loading ? (
          <Card className="p-10 text-center bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <p className="text-slate-500 dark:text-slate-400">Cargando...</p>
          </Card>
        ) : payments.length === 0 ? (
          <Card className="p-10 text-center bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" data-testid="empty-state">
            <FileText className="h-10 w-10 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400">No hay pagos con los filtros seleccionados</p>
          </Card>
        ) : grupos ? (
          <div className="space-y-3" data-testid="grouped-view">
            {grupos.map(g => {
              const isOpen = expandedGroups[g.key] !== false; // por defecto abierto
              return (
                <Card key={g.key} className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid={`group-${g.key}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedGroups(prev => ({ ...prev, [g.key]: !isOpen }))}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 dark:text-slate-100 truncate">{g.key}</p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">{g.count} pagos · Q{g.monto_pagado.toLocaleString('es-GT', { minimumFractionDigits: 2 })} pagados</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      <p className="text-lg font-bold text-slate-900 dark:text-slate-100 tabular-nums">
                        Q{g.monto_total.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">Total</p>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-slate-100 dark:border-slate-800 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400">
                          <tr>
                            <th className="text-left font-semibold px-4 py-2 uppercase text-[10px] tracking-wider">Fecha</th>
                            <th className="text-left font-semibold px-4 py-2 uppercase text-[10px] tracking-wider">Banco</th>
                            <th className="text-left font-semibold px-4 py-2 uppercase text-[10px] tracking-wider">Tienda</th>
                            <th className="text-left font-semibold px-4 py-2 uppercase text-[10px] tracking-wider">Proveedor</th>
                            <th className="text-left font-semibold px-4 py-2 uppercase text-[10px] tracking-wider">Serie/DT</th>
                            <th className="text-left font-semibold px-4 py-2 uppercase text-[10px] tracking-wider">Estado</th>
                            <th className="text-right font-semibold px-4 py-2 uppercase text-[10px] tracking-wider">Monto</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                          {g.items.map(p => <PaymentRow key={p.id} p={p} navigate={navigate} />)}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" data-testid="table-view">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Fecha</th>
                    <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Banco</th>
                    <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Tienda</th>
                    <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Proveedor</th>
                    <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Serie/DT</th>
                    <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Estado</th>
                    <th className="text-right font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Monto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {payments.map(p => <PaymentRow key={p.id} p={p} navigate={navigate} />)}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function PaymentRow({ p, navigate }) {
  let created;
  try {
    created = format(new Date(p.created_at), 'dd MMM yy', { locale: es });
  } catch { created = ''; }
  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors" onClick={() => navigate(`/payments/${p.id}`)} data-testid={`row-${p.id}`}>
      <td className="px-4 py-3 whitespace-nowrap text-slate-600 dark:text-slate-400">{created}</td>
      <td className="px-4 py-3 text-slate-800 dark:text-slate-200">{p.banco || '—'}</td>
      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{p.tienda_nombre || p.centro_costo || '—'}</td>
      <td className="px-4 py-3">
        <p className="font-medium text-slate-900 dark:text-slate-100">{p.proveedor_nombre || p.proveedor}</p>
        <p className="text-[11px] text-slate-500 dark:text-slate-500">{p.nit_proveedor}</p>
      </td>
      <td className="px-4 py-3 text-slate-600 dark:text-slate-400 whitespace-nowrap">{p.serie || '—'} · {p.numero_dt || '—'}</td>
      <td className="px-4 py-3"><StatusBadge status={p.status} size="sm" /></td>
      <td className="px-4 py-3 text-right font-semibold text-slate-900 dark:text-slate-100 tabular-nums whitespace-nowrap">
        Q{Number(p.monto_depositar || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}
      </td>
    </tr>
  );
}

function SummaryCard({ label, value, icon: Icon, tone }) {
  const toneMap = {
    blue: { grad: 'from-blue-100 to-blue-200/70 dark:from-blue-950/60 dark:to-blue-900/40', text: 'text-[#2c57c5] dark:text-blue-300' },
    emerald: { grad: 'from-emerald-100 to-emerald-200/70 dark:from-emerald-950/60 dark:to-emerald-900/40', text: 'text-emerald-600 dark:text-emerald-300' },
    amber: { grad: 'from-amber-100 to-amber-200/70 dark:from-amber-950/60 dark:to-amber-900/40', text: 'text-amber-600 dark:text-amber-300' },
  };
  const t = toneMap[tone] || toneMap.blue;
  return (
    <Card className="p-5 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1">{label}</p>
          <p className="text-xl font-bold text-slate-900 dark:text-slate-100 tabular-nums truncate">{value}</p>
        </div>
        <div className={`bg-gradient-to-br ${t.grad} rounded-xl p-3 shrink-0`}>
          <Icon className={`h-5 w-5 ${t.text}`} />
        </div>
      </div>
    </Card>
  );
}
